import { useEffect, useState } from 'react';

import { format } from 'date-fns';

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

import type { Message } from '@/models/Messages';

const MessagesAdminComponent = () => {
	const [selectedStatus, setSelectedStatus] = useState('ALL');
	const [searchQuery, setSearchQuery] = useState('');

	// Messages
	const [messages, setMessages] = useState<Message[]>([]);
	const fetchMessages = async () => {
		try {
			const response = await fetch('/api/messages');
			const data = await response.json();
			setMessages(data);
		} catch (error) {
			console.error('Failed to fetch messages:', error);
		}
	};
	useEffect(() => {
		fetchMessages();
	}, []);

	const filteredMessages = messages.filter((m) => {
		const matchStatus = selectedStatus === 'ALL' || m.status === selectedStatus;
		const matchSearch =
			m.firstname.includes(searchQuery) ||
			m.lastname.includes(searchQuery) ||
			(m.email ?? '').includes(searchQuery) ||
			(m.phone ?? '').includes(searchQuery) ||
			m.message.includes(searchQuery);
		return matchStatus && matchSearch;
	});
	const newMessagesCount = messages.filter((m) => m.status === 'NEW').length;

	// Message details — opening a message marks it READ so the NEW filter keeps
	// showing only what has not been dealt with yet.
	const [messageDetails, setMessageDetails] = useState<Message | null>(null);
	const [isMessageDetailsDialogOpen, setMessageDetailsDialogOpen] = useState(false);
	const showMessageDetailsDialog = async (message: Message) => {
		setMessageDetails(message);
		setMessageDetailsDialogOpen(true);

		if (message.status === 'NEW') {
			try {
				await fetch('/api/messages/' + message.id + '/status/READ', { method: 'PUT' });
				fetchMessages();
			} catch (error) {
				console.error('Failed to mark message as read:', error);
			}
		}
	};

	// Change message status
	const doChangeMessageStatus = async (id: number, status: string) => {
		try {
			await fetch('/api/messages/' + id + '/status/' + status, { method: 'PUT' });
			fetchMessages();
		} catch (error) {
			console.error('Failed to change message status:', error);
		}
	};

	// Delete message
	const [messageToDeleteId, setMessageToDeleteId] = useState(0);
	const [isDeleteMessageDialogOpen, setIsDeleteMessageDialogOpen] = useState(false);
	const doShowDeleteMessageDialog = (id: number) => {
		setMessageToDeleteId(id);
		setIsDeleteMessageDialogOpen(true);
	};
	const doDeleteMessage = async () => {
		try {
			await fetch('/api/messages/' + messageToDeleteId, { method: 'DELETE' });
			fetchMessages();

			setMessageToDeleteId(0);
			setIsDeleteMessageDialogOpen(false);
		} catch (error) {
			console.error('Error: ', error);
		}
	};

	return (
		<div className="min-h-screen" style={{ backgroundColor: '#e6e6b8' }}>
			<div className="top-0 z-10000 w-full px-0 py-3 sm:p-6">
				<Navbar />
			</div>

			<div className="container mx-auto py-6 sm:py-8">
				<div className="text-center font-bold text-2xl sm:text-3xl">الرسائل الواردة</div>
				<div className="pt-2 text-center font-bold" dir="rtl">
					{newMessagesCount > 0 ? 'لديك ' + newMessagesCount + ' رسالة جديدة' : 'لا توجد رسائل جديدة'}
				</div>
			</div>

			<div className="container mx-auto py-6 sm:py-8">
				{/* Filters */}
				<div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center">
					<div className="relative flex-1">
						<Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							placeholder="ابحث عن رسالة..."
							className="pr-10"
							style={{ borderColor: 'green' }}
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
						/>
					</div>
					<div className="flex gap-4 flex-wrap">
						<Button key="NEW" size="sm" variant={selectedStatus === 'NEW' ? 'default' : 'outline'} onClick={() => setSelectedStatus('NEW')}>
							جديدة
						</Button>
						<Button key="READ" size="sm" variant={selectedStatus === 'READ' ? 'default' : 'outline'} onClick={() => setSelectedStatus('READ')}>
							مقروءة
						</Button>
						<Button key="ALL" size="sm" variant={selectedStatus === 'ALL' ? 'default' : 'outline'} onClick={() => setSelectedStatus('ALL')}>
							الكل
						</Button>
					</div>
				</div>

				{/* Messages Table */}
				{/* Same treatment as the clients table: keep the cells legible on a phone
				    and let the wrapper scroll sideways. */}
				<div className="overflow-x-auto rounded-sm" style={{ borderWidth: 1, borderColor: 'green' }}>
					<Table dir="rtl" className="min-w-[900px]">
						<TableHeader>
							<TableRow>
								<TableHead className="font-bold text-foreground text-center">الرقم</TableHead>
								<TableHead className="font-bold text-foreground text-center">تاريخ الإرسال</TableHead>
								<TableHead className="font-bold text-foreground text-center">الإسم واللقب</TableHead>
								<TableHead className="font-bold text-foreground text-center">البريد الإلكتروني</TableHead>
								<TableHead className="font-bold text-foreground text-center">رقم الهاتف</TableHead>
								<TableHead className="font-bold text-foreground text-center">الرسالة</TableHead>
								<TableHead className="font-bold text-foreground text-center">الحالة</TableHead>
								<TableHead className="font-bold text-foreground text-center">#</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{filteredMessages.map((message, index) => (
								<TableRow key={message.id}>
									<TableCell className="text-center">{index + 1}</TableCell>
									<TableCell className="text-center">{format(new Date(message.create_at), 'yyyy-MM-dd')}</TableCell>
									<TableCell className="text-center">{message.lastname + ' ' + message.firstname}</TableCell>
									<TableCell className="text-center">{message.email}</TableCell>
									<TableCell className="text-center">{message.phone}</TableCell>
									{/* Clipped in JS rather than with `truncate`: that utility is not in the
								    committed src/App.css, so it would be inert until Tailwind is rebuilt. */}
								<TableCell className="max-w-[300px] text-center">
									{message.message.length > 80 ? message.message.slice(0, 80) + '…' : message.message}
								</TableCell>
									<TableCell className="text-center">
										{message.status === 'NEW' && (
											<div className="p-1 rounded-md" style={{ backgroundColor: 'mediumblue', color: 'white' }}>
												جديدة
											</div>
										)}

										{message.status === 'READ' && (
											<div className="p-1 rounded-md" style={{ backgroundColor: 'yellowgreen', color: 'black' }}>
												مقروءة
											</div>
										)}
									</TableCell>
									<TableCell>
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<Button variant="outline">...</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent>
												<DropdownMenuItem onClick={() => showMessageDetailsDialog(message)} dir="rtl">
													قراءة الرسالة
												</DropdownMenuItem>

												<DropdownMenuSeparator />

												<DropdownMenuItem onClick={() => doChangeMessageStatus(message.id, 'NEW')} dir="rtl">
													وضع كغير مقروءة
												</DropdownMenuItem>

												<DropdownMenuSeparator />

												<DropdownMenuItem onClick={() => doShowDeleteMessageDialog(message.id)} style={{ color: 'red' }} dir="rtl">
													حدف
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
						<TableFooter></TableFooter>
					</Table>
				</div>
			</div>

			{/* Message details */}
			<div dir="rtl">
				<Dialog open={isMessageDetailsDialogOpen} onOpenChange={setMessageDetailsDialogOpen}>
					<DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] max-w-[800px] overflow-y-auto p-4 sm:p-8" style={{ minHeight: 400 }} dir="rtl">
						<DialogHeader dir="rtl">
							<DialogTitle>تفاصيل الرسالة</DialogTitle>
							<DialogDescription />
						</DialogHeader>

						<div className="flex flex-col gap-4 sm:flex-row">
							<div className="w-full min-w-0 space-y-2">
								<Label htmlFor="firstname" className="font-bold">
									الإسم
								</Label>
								<Input id="firstname" type="text" className="text-right" style={{ borderColor: 'green' }} value={messageDetails?.firstname ?? ''} readOnly />
							</div>
							<div className="w-full min-w-0 space-y-2">
								<Label htmlFor="lastname" className="font-bold">
									اللقب
								</Label>
								<Input id="lastname" type="text" className="text-right" style={{ borderColor: 'green' }} value={messageDetails?.lastname ?? ''} readOnly />
							</div>
						</div>

						<div className="flex flex-col gap-4 sm:flex-row">
							<div className="w-full min-w-0 space-y-2">
								<Label htmlFor="email" className="font-bold">
									البريد الإلكتروني
								</Label>
								<Input id="email" type="text" className="text-right" style={{ borderColor: 'green' }} value={messageDetails?.email ?? ''} readOnly />
							</div>
							<div className="w-full min-w-0 space-y-2">
								<Label htmlFor="phone" className="font-bold">
									رقم الهاتف
								</Label>
								<Input id="phone" type="text" className="text-right" style={{ borderColor: 'green' }} value={messageDetails?.phone ?? ''} readOnly />
							</div>
						</div>

						<div className="w-full min-w-0 space-y-2">
							<Label htmlFor="createDate" className="font-bold">
								تاريخ الإرسال
							</Label>
							<Input
								id="createDate"
								type="text"
								className="text-right"
								style={{ borderColor: 'green' }}
								value={messageDetails ? format(new Date(messageDetails.create_at), 'yyyy-MM-dd HH:mm') : ''}
								readOnly
							/>
						</div>

						<div className="w-full min-w-0 space-y-2">
							<Label htmlFor="message" className="font-bold">
								الرسالة
							</Label>
							<Textarea id="message" className="text-right" style={{ borderColor: 'green', minHeight: 160 }} value={messageDetails?.message ?? ''} readOnly />
						</div>

						<DialogFooter className="sm:justify-start" dir="rtl">
							{messageDetails?.email && (
								<a href={'mailto:' + messageDetails.email}>
									<Button size="sm">الرد عبر البريد الإلكتروني</Button>
								</a>
							)}
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>

			{/* Message delete alert dialog */}
			<div dir="rtl">
				<AlertDialog open={isDeleteMessageDialogOpen} onOpenChange={setIsDeleteMessageDialogOpen}>
					<AlertDialogContent dir="rtl">
						<div dir="rtl">
							<AlertDialogHeader dir="rtl">
								<AlertDialogTitle>حدف الرسالة</AlertDialogTitle>
							</AlertDialogHeader>
						</div>
						<AlertDialogFooter dir="rtl">
							<div className="flex flex-row gap-2">
								<Button size="sm" variant="outline" onClick={() => setIsDeleteMessageDialogOpen(false)}>
									الغاء
								</Button>
								<Button size="sm" variant="destructive" onClick={() => doDeleteMessage()}>
									أحدف
								</Button>
							</div>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</div>

			<Footer />
		</div>
	);
};

export default MessagesAdminComponent;
